import React, { useEffect, useState } from 'react';
import DetailBar from './components/DetailBar';
import EditorialSummaryChart from '../../components/EditorialSummaryChart';
import ComparisonControls from '../../components/ComparisonControls';
import moment from 'moment';
import { Col, Row } from 'antd';
import SessionsTable from './components/SessionsTable';
import SessionDeviceBreakdown from './components/SessionDeviceBreakdown';
import { LoadingPage } from '../../../../../shared/LoadingPage';
import {
  COMPARISON_MODES,
  getComparisonRange,
  buildComparisonSeries,
  computeDeltas,
} from '../../utils/comparison';

// Delta keys are the summary fields; chart keys are the sessionsByDate fields.
const SESSION_DELTA_KEYS = ['sessions', 'avgSessionDuration', 'pagesPerSession', 'sessionAttrPageViews'];
const SESSION_CHART_KEYS = ['sessions', 'totalSessionDuration', 'averageSessionDuration', 'pagesPerSession'];

const day = (d) => moment(d).format('YYYY-MM-DD');

// Reduce the (full) sessionsList to summary totals for a range — mirrors the
// parent's changeSessionsDatePicker so current + comparison match exactly.
const computeSessionsSummary = (sessionsList, [start, end]) => {
  const startStr = day(start);
  const endStr = day(end);
  const filtered = (sessionsList || []).filter((s) => {
    const t = moment(s.start, 'YYYY-MM-DD');
    return t.isSameOrAfter(startStr) && t.isSameOrBefore(endStr);
  });
  const len = filtered.length;
  const totalDuration = filtered.reduce((acc, s) => acc + (Number(s.durationInSeconds) || 0), 0);
  const totalPageViews = filtered.reduce((acc, s) => acc + (Number(s.pageViews) || 0), 0);
  const totalClicks = filtered.reduce((acc, s) => acc + (Number(s.clicks) || 0), 0);
  return {
    sessions: len,
    avgSessionDuration: len > 0 ? totalDuration / len : 0,
    pagesPerSession: len > 0 ? totalPageViews / len : 0,
    sessionAttrPageViews: totalClicks,
  };
};

const filterByRange = (rows, field, [start, end]) => {
  const startStr = day(start);
  const endStr = day(end);
  return (rows || []).filter((r) => {
    const t = moment(r[field], 'YYYY-MM-DD');
    return t.isSameOrAfter(startStr) && t.isSameOrBefore(endStr);
  });
};

const EditorialSessionsDetailsView = ({
  data,
  sessionsSummary = {},
  startDate,
  endDate,
  sessionsList,
  sessionsByDates,
  deviceBreakdown,
  changeSessionsDatePicker,
}) => {
  const { sessions, avgSessionDuration, pagesPerSession, sessionAttrPageViews } = sessionsSummary;
  const dropDownOptions = [
    { key: 'sessions', display: 'Sessions' },
    { key: 'totalSessionDuration', display: 'Total Session Duration' },
    { key: 'averageSessionDuration', display: 'Average Session Duration' },
    { key: 'pagesPerSession', display: 'Pages Per Session' },
  ];

  const [currentRange, setCurrentRange] = useState([moment(startDate), moment(endDate)]);
  const [compareMode, setCompareMode] = useState(COMPARISON_MODES.OFF);
  const [customRange, setCustomRange] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [comparisonByDate, setComparisonByDate] = useState([]);

  useEffect(() => {
    if (!data || !compareMode || compareMode === COMPARISON_MODES.OFF) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const comparisonRange = getComparisonRange(compareMode, currentRange, customRange);
    if (!comparisonRange) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const curSummary = computeSessionsSummary(data.sessionsList, currentRange);
    const cmpSummary = computeSessionsSummary(data.sessionsList, comparisonRange);
    setDeltas(computeDeltas(curSummary, cmpSummary, SESSION_DELTA_KEYS, comparisonRange));
    setComparisonByDate(
      buildComparisonSeries(
        filterByRange(data.sessionsByDate, 'date', currentRange),
        filterByRange(data.sessionsByDate, 'date', comparisonRange),
        currentRange,
        comparisonRange,
        SESSION_CHART_KEYS,
        'date'
      )
    );
  }, [data, compareMode, customRange, currentRange]);

  if (!data) return <LoadingPage />;

  const comparisonLabel = deltas && deltas.sessions ? deltas.sessions.ranges : '';

  return (
    <>
      <DetailBar
        sessionAttrPageViews={sessionAttrPageViews}
        startDate={startDate}
        endDate={endDate}
        sessions={sessions}
        averageSessionDuration={avgSessionDuration}
        pagesPerSession={pagesPerSession}
        loading={false}
        module="SESSIONS"
        deltas={deltas}
      />
      <ComparisonControls
        mode={compareMode}
        onModeChange={(mode) => setCompareMode(mode)}
        customRange={customRange}
        onCustomRangeChange={(dates) => setCustomRange(dates)}
        comparisonLabel={comparisonLabel}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <EditorialSummaryChart
            id="sessions"
            byDate={sessionsByDates || []}
            xAxis="date"
            options={dropDownOptions}
            defaultLeftYValue={dropDownOptions[0]}
            defaultRightYValue={dropDownOptions[1]}
            onDatePickerChange={({ dates }) => {
              setCurrentRange(dates);
              changeSessionsDatePicker(dates);
            }}
            has6MonDisables={false}
            allTimeDateRange={[moment(startDate), moment(endDate)]}
            comparisonByDate={comparisonByDate}
          />
        </Col>
        <Col span={24}>
          <SessionDeviceBreakdown data={deviceBreakdown || null} />
        </Col>
        <Col xs={24}>
          <SessionsTable data={sessionsList || []} />
        </Col>
      </Row>
    </>
  );
};

export default EditorialSessionsDetailsView;
